/**
 * Frontend permission helper — UX convenience ONLY.
 * The backend re-checks every permission (see docs/security.md). Never rely on
 * this for security.
 */

export type RoleName =
  | "OWNER"
  | "ADMIN"
  | "MANAGER"
  | "MEDIA_BUYER"
  | "CREATIVE"
  | "ACCOUNTANT"
  | "VIEWER";

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  roles: RoleName[];
  /** flattened permission keys, e.g. ["clients.view", "payments.create"] */
  permissions: string[];
  /**
   * Spec §3 — clés explicitement REFUSÉES pour ce membre (exceptions
   * individuelles en mode DENY). Absentes des jetons anciens : le champ reste
   * optionnel pour ne pas casser une session déjà ouverte.
   */
  deniedPermissions?: string[];
}

/** Owner/Admin bypass: treated as having every permission. */
const SUPER_ROLES: RoleName[] = ["OWNER", "ADMIN"];

/** Roles that see the full agency dashboard, can assign tasks and manage the team. */
const AGENCY_ADMIN_ROLES: RoleName[] = ["OWNER", "ADMIN", "MANAGER"];

export function can(user: CurrentUser | null | undefined, permission: string): boolean {
  if (!user) return false;
  // Spec §3 — les restrictions explicites sont la DERNIÈRE étape de résolution :
  // un refus l'emporte sur les rôles ET sur le bypass OWNER/ADMIN. Sans ce
  // test, poser un DENY sur un propriétaire ne masquait rien dans l'interface.
  if (user.deniedPermissions?.includes(permission)) return false;
  if (user.roles.some((r) => SUPER_ROLES.includes(r))) return true;
  return user.permissions.includes(permission);
}

export function canAny(user: CurrentUser | null | undefined, permissions: string[]): boolean {
  return permissions.some((p) => can(user, p));
}

/** True for OWNER / ADMIN / MANAGER — those who see the full agency dashboard. */
export function isAgencyAdmin(user: CurrentUser | null | undefined): boolean {
  if (!user) return false;
  return user.roles.some((r) => AGENCY_ADMIN_ROLES.includes(r));
}

